import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import './Reports.css';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface DailyReportData {
  date: string;
  offer: string;
  offerId: number;
  advertiser: string;
  affiliate: string;
  affiliateId: number;
  clicks: number;
  impressions: number;
  conversions: number;
  postbacksFired: number;
  postbacksNotFired: number;
  postbackFireRate: string;
  cvr: number;
  revenue: number;
  payout: number;
  profit: number;
  epc: number;
}

const DailyReport: React.FC = () => {
  // Set default date range to today only
  const getDefaultDates = () => {
    const today = new Date();
    return {
      startDate: today.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  };

  const [dateRange, setDateRange] = useState(getDefaultDates());
  const [groupBy, setGroupBy] = useState<'date' | 'offer' | 'affiliate' | 'advertiser'>('date');
  const [searchTerm, setSearchTerm] = useState('');
  const [reportData, setReportData] = useState<DailyReportData[]>([]);
  const [loading, setLoading] = useState(true);
  const [todayStats, setTodayStats] = useState({ clicks: 0, conversions: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);

        // Fetch main report data with pagination
        const response = await apiService.getDailyReport({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          groupBy: groupBy,
          limit: itemsPerPage,
          page: currentPage
        });
        setReportData(response.data.map((row: any) => ({
          ...row,
          cvr: parseFloat(row.cvr),
          revenue: parseFloat(row.revenue),
          payout: parseFloat(row.payout),
          profit: parseFloat(row.profit),
          epc: parseFloat(row.epc)
        })));

        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
          setTotalCount(response.pagination.total);
        }

        // Fetch today's stats separately (without pagination)
        const today = new Date().toISOString().split('T')[0];
        const todayResponse = await apiService.getDailyReport({
          startDate: today,
          endDate: today,
          groupBy: 'date',
          limit: 10000 // Large limit to get all today's data
        });

        if (todayResponse.data.length > 0) {
          // Aggregate all rows for today to get total clicks and conversions
          const totalClicks = todayResponse.data.reduce((sum: number, row: any) => sum + (row.clicks || 0), 0);
          const totalConversions = todayResponse.data.reduce((sum: number, row: any) => sum + (row.conversions || 0), 0);
          setTodayStats({
            clicks: totalClicks,
            conversions: totalConversions
          });
        } else {
          setTodayStats({ clicks: 0, conversions: 0 });
        }
      } catch (error: any) {
        console.error('Failed to fetch daily report:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [dateRange, groupBy, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, groupBy, searchTerm]);

  const filteredData = reportData.filter(row => {
    const searchMatch = row.offer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       row.advertiser.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       row.affiliate.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       row.date.includes(searchTerm);

    // Skip date filtering when groupBy is not 'date' (date will be '-')
    const dateMatch = groupBy === 'date'
      ? (row.date >= dateRange.startDate && row.date <= dateRange.endDate)
      : true;

    return searchMatch && dateMatch;
  });

  const calculateTotals = () => {
    return filteredData.reduce((totals, row) => ({
      clicks: totals.clicks + row.clicks,
      impressions: totals.impressions + row.impressions,
      conversions: totals.conversions + row.conversions,
      revenue: totals.revenue + row.revenue,
      payout: totals.payout + row.payout,
      profit: totals.profit + row.profit
    }), { clicks: 0, impressions: 0, conversions: 0, revenue: 0, payout: 0, profit: 0 });
  };

  const totals = calculateTotals();
  const avgCvr = totals.clicks > 0 ? (totals.conversions / totals.clicks * 100) : 0;
  const avgEpc = totals.clicks > 0 ? (totals.revenue / totals.clicks) : 0;

  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const exportToCSV = () => {
    const headers = [
      'Date', 'Offer', 'Offer ID', 'Advertiser', 'Affiliate', 'Affiliate ID',
      'Impressions', 'Clicks', 'Conversions', 'PB Fired', 'PB Not Fired',
      'PB Rate', 'CVR', 'EPC', 'Revenue', 'Payout', 'Profit'
    ];

    const csvData = filteredData.map(row => [
      row.date,
      row.offer,
      row.offerId,
      row.advertiser,
      row.affiliate,
      row.affiliateId,
      row.impressions,
      row.clicks,
      row.conversions,
      row.postbacksFired,
      row.postbacksNotFired,
      row.postbackFireRate,
      row.cvr.toFixed(2),
      row.epc.toFixed(3),
      row.revenue.toFixed(2),
      row.payout.toFixed(2),
      row.profit.toFixed(2)
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `daily_report_${dateRange.startDate}_to_${dateRange.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredData.map(row => ({
        'Date': row.date,
        'Offer': row.offer,
        'Offer ID': row.offerId,
        'Advertiser': row.advertiser,
        'Affiliate': row.affiliate,
        'Affiliate ID': row.affiliateId,
        'Impressions': row.impressions,
        'Clicks': row.clicks,
        'Conversions': row.conversions,
        'PB Fired': row.postbacksFired,
        'PB Not Fired': row.postbacksNotFired,
        'PB Rate': row.postbackFireRate + '%',
        'CVR': row.cvr.toFixed(2) + '%',
        'EPC': '$' + row.epc.toFixed(3),
        'Revenue': '$' + row.revenue.toFixed(2),
        'Payout': '$' + row.payout.toFixed(2),
        'Profit': '$' + row.profit.toFixed(2)
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Daily Report');

    XLSX.writeFile(workbook, `daily_report_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF('landscape');

    doc.setFontSize(18);
    doc.text('Daily Report', 14, 15);
    doc.setFontSize(11);
    doc.text(`Period: ${dateRange.startDate} to ${dateRange.endDate}`, 14, 22);

    const tableData = filteredData.map(row => [
      row.date,
      row.offer,
      row.offerId,
      row.advertiser,
      row.affiliate,
      row.affiliateId,
      row.impressions.toLocaleString(),
      row.clicks.toLocaleString(),
      row.conversions.toLocaleString(),
      row.postbacksFired.toLocaleString(),
      row.postbacksNotFired.toLocaleString(),
      row.postbackFireRate + '%',
      row.cvr.toFixed(2) + '%',
      '$' + row.epc.toFixed(3),
      '$' + row.revenue.toFixed(2),
      '$' + row.payout.toFixed(2),
      '$' + row.profit.toFixed(2)
    ]);

    autoTable(doc, {
      head: [[
        'Date', 'Offer', 'Offer ID', 'Advertiser', 'Affiliate', 'Affiliate ID',
        'Impressions', 'Clicks', 'Conversions', 'PB Fired', 'PB Not Fired',
        'PB Rate', 'CVR', 'EPC', 'Revenue', 'Payout', 'Profit'
      ]],
      body: tableData,
      startY: 28,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 }
    });

    doc.save(`daily_report_${dateRange.startDate}_to_${dateRange.endDate}.pdf`);
  };

  return (
    <div className="reports-page">
      <div className="page-header">
        <h1>Daily Report</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Refresh Data
          </button>
        </div>
      </div>

      {/* Today's Stats - Highlighted Section */}
      <div className="report-summary" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '20px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '15px' }}>
          <h2 style={{ color: 'white', margin: 0, fontSize: '24px' }}>
            Today's Performance - {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </h2>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '40px' }}>
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', margin: '0 0 8px 0' }}>Today's Clicks</h3>
            <div style={{ color: 'white', fontSize: '36px', fontWeight: 'bold' }}>{todayStats.clicks.toLocaleString()}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', margin: '0 0 8px 0' }}>Today's Conversions</h3>
            <div style={{ color: 'white', fontSize: '36px', fontWeight: 'bold' }}>{todayStats.conversions.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="report-summary">
        <div className="summary-card">
          <h3>Total Clicks</h3>
          <div className="value">{totals.clicks.toLocaleString()}</div>
        </div>
        <div className="summary-card">
          <h3>Total Conversions</h3>
          <div className="value">{totals.conversions.toLocaleString()}</div>
        </div>
        <div className="summary-card">
          <h3>Average CVR</h3>
          <div className="value">{avgCvr.toFixed(2)}%</div>
        </div>
        <div className="summary-card">
          <h3>Total Revenue</h3>
          <div className="value">${totals.revenue.toFixed(2)}</div>
        </div>
        <div className="summary-card">
          <h3>Total Payout</h3>
          <div className="value">${totals.payout.toFixed(2)}</div>
        </div>
        <div className="summary-card">
          <h3>Total Profit</h3>
          <div className="value">${totals.profit.toFixed(2)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="report-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label>Start Date:</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => handleDateChange('startDate', e.target.value)}
              className="date-input"
            />
          </div>
          <div className="filter-group">
            <label>End Date:</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => handleDateChange('endDate', e.target.value)}
              className="date-input"
            />
          </div>
          <div className="filter-group">
            <label>Group By:</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as any)}
              className="group-select"
            >
              <option value="date">Date</option>
              <option value="offer">Offer</option>
              <option value="affiliate">Affiliate</option>
              <option value="advertiser">Advertiser</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Search offers, advertisers, affiliates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="export-buttons">
          <button className="btn btn-success" onClick={exportToCSV}>
            CSV
          </button>
          <button className="btn btn-warning" onClick={exportToExcel}>
            Excel
          </button>
          <button className="btn btn-danger" onClick={exportToPDF}>
            PDF
          </button>
        </div>
      </div>

      {/* Report Table */}
      <div className="report-content">
        <div className="table-container">
          <table className="report-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Offer</th>
                <th>Advertiser</th>
                <th>Affiliate</th>
                <th>Impressions</th>
                <th>Clicks</th>
                <th>Conversions</th>
                <th>PB Fired</th>
                <th>PB Not Fired</th>
                <th>PB Rate</th>
                <th>CVR</th>
                <th>EPC</th>
                <th>Revenue</th>
                <th>Payout</th>
                <th>Profit</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((row, index) => (
                <tr key={index}>
                  <td>{row.date}</td>
                  <td>
                    <div className="offer-cell">
                      <span className="offer-name">{row.offer}</span>
                      <span className="offer-id">ID: {row.offerId}</span>
                    </div>
                  </td>
                  <td>{row.advertiser}</td>
                  <td>
                    <div className="affiliate-cell">
                      <span className="affiliate-name">{row.affiliate}</span>
                      <span className="affiliate-id">ID: {row.affiliateId}</span>
                    </div>
                  </td>
                  <td>{row.impressions.toLocaleString()}</td>
                  <td>{row.clicks.toLocaleString()}</td>
                  <td>{row.conversions.toLocaleString()}</td>
                  <td>
                    <span className="approved">{row.postbacksFired.toLocaleString()}</span>
                  </td>
                  <td>
                    <span className="rejected">{row.postbacksNotFired.toLocaleString()}</span>
                  </td>
                  <td>
                    <span className="postback-rate">{row.postbackFireRate}%</span>
                  </td>
                  <td>
                    <span className="cvr">{row.cvr.toFixed(2)}%</span>
                  </td>
                  <td>
                    <span className="epc">${row.epc.toFixed(3)}</span>
                  </td>
                  <td>
                    <span className="revenue">${row.revenue.toFixed(2)}</span>
                  </td>
                  <td>
                    <span className="payout">${row.payout.toFixed(2)}</span>
                  </td>
                  <td>
                    <span className="profit">${row.profit.toFixed(2)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="totals-row">
                <td><strong>TOTALS</strong></td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td><strong>{totals.impressions.toLocaleString()}</strong></td>
                <td><strong>{totals.clicks.toLocaleString()}</strong></td>
                <td><strong>{totals.conversions.toLocaleString()}</strong></td>
                <td><strong>-</strong></td>
                <td><strong>-</strong></td>
                <td><strong>-</strong></td>
                <td><strong>{avgCvr.toFixed(2)}%</strong></td>
                <td><strong>${avgEpc.toFixed(3)}</strong></td>
                <td><strong>${totals.revenue.toFixed(2)}</strong></td>
                <td><strong>${totals.payout.toFixed(2)}</strong></td>
                <td><strong>${totals.profit.toFixed(2)}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="table-footer">
        <div className="table-info">
          Showing {filteredData.length > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries for {dateRange.startDate} to {dateRange.endDate}
        </div>
        <div className="pagination">
          <button
            className="page-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </button>
            );
          })}
          <button
            className="page-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default DailyReport;